import React, { useEffect, useState } from "react";
import { ShieldCheck, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

function AgentLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", twofa_code: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const existing = JSON.parse(localStorage.getItem("agent") || "null");
    if (existing?.id) navigate("/agent-panel", { replace: true });
  }, [navigate]);

  const submit = async () => {
    try {
      setLoading(true);
      const res = await API.post("/agent-login", form);
      localStorage.setItem("agent", JSON.stringify(res.data.agent));
      navigate("/agent-panel", { replace: true });
    } catch (err) {
      alert(err?.response?.data?.message || "Agent login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={page}>
      <div style={backGlow} />
      <div style={sidePattern} />
      <section style={heroPanel}>
        <div style={brandStack}>
          <img src="/brand-logo.png" alt="Matka Kings" style={brandLogo} />
          <div>
            <p style={eyebrow}>Agent Portal</p>
            <h1 style={heroTitle}>Manage players, transactions, and referrals from one clean workspace.</h1>
            <p style={heroText}>Secure access for Matka Kings agents with 2FA verification and branded dashboard tools.</p>
          </div>
          <div style={heroChips}>
            <span style={heroChip}><UserRound size={14} /> Client network</span>
            <span style={heroChip}><ShieldCheck size={14} /> Secure agent login</span>
          </div>
        </div>

        <div style={loginCard}>
          <div style={loginHeader}>
            <img src="/mk-logo192.png" alt="Matka Kings" style={loginLogo} />
            <div>
              <strong style={loginTitle}>Agent Login</strong>
              <p style={loginSubtext}>Use your agent email, password, and 2FA code.</p>
            </div>
          </div>

          <label style={field}>
            <span style={fieldLabel}>Email</span>
            <input style={input} placeholder="agent@matkakings.com" value={form.email} onChange={(e) => setForm((c) => ({ ...c, email: e.target.value }))} />
          </label>

          <label style={field}>
            <span style={fieldLabel}>Password</span>
            <input style={input} placeholder="Enter password" type="password" value={form.password} onChange={(e) => setForm((c) => ({ ...c, password: e.target.value }))} />
          </label>

          <label style={field}>
            <span style={fieldLabel}>2FA Code</span>
            <input style={input} placeholder="4 digit code" value={form.twofa_code} onChange={(e) => setForm((c) => ({ ...c, twofa_code: e.target.value.replace(/\D/g, "").slice(0, 4) }))} />
          </label>

          <button style={button} onClick={submit} disabled={loading}>
            {loading ? "Signing in..." : "Login to Agent Dashboard"}
          </button>
        </div>
      </section>
    </div>
  );
}

const page = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  position: "relative",
  overflow: "hidden",
  background: "radial-gradient(circle at top left, rgba(255,34,69,.18), transparent 28%), radial-gradient(circle at right, rgba(88,126,255,.18), transparent 26%), linear-gradient(135deg, #0b0b11, #13141c 48%, #0d0f16)",
  padding: "24px",
};

const backGlow = {
  position: "absolute",
  inset: "auto auto -220px -180px",
  width: 520,
  height: 520,
  borderRadius: "50%",
  background: "radial-gradient(circle, rgba(255,35,70,.28), transparent 70%)",
  filter: "blur(20px)",
  pointerEvents: "none",
};

const sidePattern = {
  position: "absolute",
  inset: "0 0 0 auto",
  width: "42vw",
  background: "linear-gradient(180deg, rgba(255,255,255,.03), transparent), repeating-linear-gradient(135deg, rgba(255,255,255,.02) 0 18px, transparent 18px 36px)",
  opacity: 0.7,
  pointerEvents: "none",
};

const heroPanel = {
  position: "relative",
  zIndex: 1,
  width: "min(1180px, 100%)",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.15fr) minmax(360px, 430px)",
  gap: 28,
  alignItems: "center",
};

const brandStack = {
  color: "#fff",
  padding: "18px 10px 18px 8px",
  display: "grid",
  gap: 18,
};

const brandLogo = { width: "min(360px, 78vw)", height: "auto", objectFit: "contain" };
const eyebrow = { margin: 0, color: "#ff7a8f", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", fontSize: 12 };
const heroTitle = { margin: 0, fontSize: "clamp(30px, 4vw, 54px)", lineHeight: 1.04, fontWeight: 900, maxWidth: 640 };
const heroText = { margin: 0, color: "rgba(255,255,255,.72)", fontSize: 16, lineHeight: 1.65, maxWidth: 560 };
const heroChips = { display: "flex", flexWrap: "wrap", gap: 12 };
const heroChip = {
  minHeight: 42,
  padding: "0 14px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.1)",
  background: "rgba(255,255,255,.04)",
  color: "#e8edf6",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 700,
};

const loginCard = {
  borderRadius: 28,
  padding: 24,
  display: "grid",
  gap: 16,
  background: "linear-gradient(180deg, rgba(19,24,35,.96), rgba(10,13,21,.98))",
  border: "1px solid rgba(255,255,255,.08)",
  boxShadow: "0 28px 80px rgba(0,0,0,.4)",
  backdropFilter: "blur(18px)",
};

const loginHeader = { display: "flex", alignItems: "center", gap: 14, marginBottom: 4 };
const loginLogo = { width: 56, height: 56, borderRadius: 16, objectFit: "cover", boxShadow: "0 10px 26px rgba(255,35,70,.22)" };
const loginTitle = { display: "block", color: "#fff", fontSize: 24, fontWeight: 900 };
const loginSubtext = { margin: "4px 0 0", color: "#8e9ab1", fontSize: 13, lineHeight: 1.5 };
const field = { display: "grid", gap: 7 };
const fieldLabel = { color: "#d5dceb", fontWeight: 700, fontSize: 13 };
const input = {
  minHeight: 52,
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.04)",
  color: "#fff",
  padding: "0 15px",
  outline: "none",
  fontSize: 15,
};
const button = {
  minHeight: 54,
  border: 0,
  borderRadius: 16,
  background: "linear-gradient(135deg, #ff2548, #d10f34)",
  color: "#fff",
  fontWeight: 900,
  fontSize: 15,
  letterSpacing: ".01em",
  boxShadow: "0 16px 32px rgba(209,15,52,.28)",
  cursor: "pointer",
};

export default AgentLogin;
